import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { FALLBACK_IMAGE } from '../components/Products/productService';

const ShopContext = createContext(null);

const ensureArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }
  if (value === undefined || value === null) {
    return [];
  }
  return [value];
};

const normalizeProduct = (product) => {
  if (!product || typeof product !== 'object') {
    return null;
  }

  const id = product.id || product.productCode;
  if (!id) {
    return null;
  }

  const price = typeof product.price === 'number' ? product.price : Number(product.price) || 0;
  const imageUrls = Array.isArray(product.imageUrls) && product.imageUrls.length > 0
    ? product.imageUrls
    : product.image
    ? [product.image]
    : [FALLBACK_IMAGE];

  const rawVariants = ensureArray(product.variants);
  const normalizedVariants = rawVariants
    .map((variant, index) => {
      if (!variant || typeof variant !== 'object') {
        return null;
      }

      const variantId = variant.id || `${id}-variant-${index}`;
      const size = variant.size || variant.variantSize || null;
      const color = variant.color || variant.variantColor || null;
      const stock = typeof variant.stock === 'number'
        ? variant.stock
        : typeof variant.quantity === 'number'
        ? variant.quantity
        : null;

      return {
        id: variantId,
        size,
        color,
        stock,
        raw: variant,
      };
    })
    .filter(Boolean);

  const variantColors = normalizedVariants
    .map((variant) => variant.color)
    .filter(Boolean);
  const variantSizes = normalizedVariants
    .map((variant) => variant.size)
    .filter(Boolean);

  const unique = (list) => Array.from(new Set(list));
  const colors = variantColors.length > 0 ? unique(variantColors) : ensureArray(product.colors);
  const sizes = variantSizes.length > 0 ? unique(variantSizes) : ensureArray(product.sizes);

  return {
    ...product,
    id,
    productCode: product.productCode || id,
    name: product.name || product.title || 'Produkti',
    title: product.title || product.name || 'Produkti',
    price,
    image: product.image || imageUrls[0] || FALLBACK_IMAGE,
    imageUrls,
    description: product.description || product.shortDescription || '',
    selectedSize: product.selectedSize || null,
    selectedColor: product.selectedColor || null,
    variants: normalizedVariants,
    colors,
    sizes,
  };
};

const STORAGE_KEYS = {
  wishlist: 'autokaqandolli:wishlist',
  cart: 'autokaqandolli:cart',
};

const STORAGE_TTL_MS = 24 * 60 * 60 * 1000;

const stripVariantRaw = (variant) => {
  if (!variant || typeof variant !== 'object') {
    return variant;
  }
  const { raw, ...rest } = variant;
  return rest;
};

const stripRaw = (item) => {
  if (!item || typeof item !== 'object') {
    return null;
  }
  const { raw, ...rest } = item;
  const sanitized = { ...rest };
  if (Array.isArray(rest.variants)) {
    sanitized.variants = rest.variants.map(stripVariantRaw);
  }
  return sanitized;
};

const safeParseJson = (value) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    console.warn('Failed to parse JSON from storage', error);
    return null;
  }
};

const readStoredList = (key) => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const rawValue = window.localStorage.getItem(key);
    if (!rawValue) {
      return null;
    }
    const parsed = safeParseJson(rawValue);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    const { items, timestamp } = parsed;
    if (!Array.isArray(items) || typeof timestamp !== 'number') {
      return null;
    }
    if (Date.now() - timestamp > STORAGE_TTL_MS) {
      window.localStorage.removeItem(key);
      return null;
    }
    return items;
  } catch (error) {
    console.warn('Failed to read storage key', key, error);
    return null;
  }
};

const persistList = (key, items) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const payload = {
      timestamp: Date.now(),
      items,
    };
    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch (error) {
    console.warn('Failed to persist storage key', key, error);
  }
};

const ensurePositiveQuantity = (value) => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const sanitizeWishlistItems = (items = []) =>
  items
    .map((item) => normalizeProduct(item))
    .filter(Boolean);

const sanitizeCartItems = (items = []) =>
  items
    .map((item) => {
      const normalized = normalizeProduct(item);
      if (!normalized) {
        return null;
      }
      return {
        ...normalized,
        quantity: ensurePositiveQuantity(item.quantity),
      };
    })
    .filter(Boolean);

export const ShopProvider = ({ children, initialCartItems = [], initialWishlist = [] }) => {
  const [wishlist, setWishlist] = useState(() => {
    const stored = readStoredList(STORAGE_KEYS.wishlist);
    if (stored) {
      const normalized = sanitizeWishlistItems(stored);
      if (normalized.length) {
        return normalized;
      }
    }
    return sanitizeWishlistItems(initialWishlist);
  });

  const [cartItems, setCartItems] = useState(() => {
    const stored = readStoredList(STORAGE_KEYS.cart);
    if (stored) {
      const normalized = sanitizeCartItems(stored);
      if (normalized.length) {
        return normalized;
      }
    }
    return sanitizeCartItems(initialCartItems);
  });

  useEffect(() => {
    const payload = wishlist
      .map(stripRaw)
      .filter(Boolean);
    persistList(STORAGE_KEYS.wishlist, payload);
  }, [wishlist]);

  useEffect(() => {
    const payload = cartItems
      .map((item) => {
        const sanitized = stripRaw(item);
        if (!sanitized) {
          return null;
        }
        return {
          ...sanitized,
          quantity: ensurePositiveQuantity(item.quantity),
        };
      })
      .filter(Boolean);
    persistList(STORAGE_KEYS.cart, payload);
  }, [cartItems]);

  const toggleWishlist = (product) => {
    const normalized = normalizeProduct(product);
    if (!normalized) {
      return;
    }

    setWishlist((prev) => {
      const exists = prev.some((item) => item.id === normalized.id);
      if (exists) {
        return prev.filter((item) => item.id !== normalized.id);
      }
      return [...prev, normalized];
    });
  };

  const isInWishlist = (productId) => {
    if (!productId) {
      return false;
    }
    const idAsString = productId.toString();
    return wishlist.some((item) => item.id.toString() === idAsString);
  };

  const addToCart = (product, quantity = 1) => {
    const normalized = normalizeProduct(product);
    if (!normalized) {
      return;
    }

    const safeQuantity = Math.max(1, quantity);

    setCartItems((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === normalized.id);
      if (existingIndex >= 0) {
        const next = [...prev];
        const existing = next[existingIndex];
        next[existingIndex] = {
          ...existing,
          quantity: existing.quantity + safeQuantity,
        };
        return next;
      }

      return [...prev, { ...normalized, quantity: safeQuantity }];
    });
  };

  const updateCartQuantity = (productId, quantity) => {
    const safeQuantity = Math.max(1, quantity);
    setCartItems((prev) =>
      prev.map((item) =>
        item.id === productId
          ? {
              ...item,
              quantity: safeQuantity,
            }
          : item
      )
    );
  };

  const removeFromCart = (productId) => {
    setCartItems((prev) => prev.filter((item) => item.id !== productId));
  };

  const clearCart = () => setCartItems([]);

  const subtotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [cartItems]
  );

  const value = useMemo(
    () => ({
      wishlist,
      cartItems,
      subtotal,
      toggleWishlist,
      isInWishlist,
      addToCart,
      updateCartQuantity,
      removeFromCart,
      clearCart,
    }),
    [wishlist, cartItems, subtotal]
  );

  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
};

export const useShop = () => {
  const context = useContext(ShopContext);
  if (!context) {
    throw new Error('useShop must be used within a ShopProvider');
  }
  return context;
};
